import React, { useState, useEffect } from 'react'
import { Modal, Select, Upload } from 'antd'
import Cancel from 'assets/Images/close.svg'
import VectorImg from 'assets/Images/Vector.svg'
import CsvImg from 'assets/Images/pdf 1.svg'
import CheckImg from 'assets/Images/checked.svg'
import warningImg from 'assets/Images/ImportWarning.svg'
import plusImg from 'assets/Images/ImportPlus.svg'
import { successMessage, failureMessage } from 'api/global'
import { read, utils } from 'xlsx'
import { useImportRegistrations } from 'api/Event/Attendance'
import { eventAttendanceStore } from 'state/event/eventAttendance'
import { useParams } from 'react-router-dom'
import Loader from 'helper/Loader'
import 'components/Header/importFamilyStyle.scss'

const { Option } = Select

const REGISTRATION_ATTRS = [
  { key: 'eventId', display: 'Event ID', required: true },
  { key: 'eventName', display: 'Event Name', required: false },
  { key: 'familyId', display: 'Family Id', required: true },
  { key: 'familyName', display: 'Family Name', required: false },
  { key: 'personId', display: 'Person Id', required: true },
  { key: 'personName', display: 'Person Name / Child Name', required: false },
  { key: 'personType', display: 'Person Type (kid / guardian / authorizedAdult)', required: true },
  { key: 'organizationId', display: 'Organization Id', required: true },
  { key: 'organizationName', display: 'Organization Name', required: false },
  { key: 'admissionTicketId', display: 'Admission Ticket Id', required: true },
  { key: 'days', display: 'Days (comma-separated ISO dates e.g. 2024-07-01)', required: true },
  { key: 'additionalTicketId', display: 'Additional Ticket Id (one per row)', required: false },
  { key: 'additionalDays', display: 'Additional Ticket Days (comma-separated)', required: false },
]

const ImportRegistrationsModal = () => {
  const { id: eventId }: { id: string } = useParams()

  const { importRegistrationsModalOpen, setImportRegistrationsModalOpen } =
    eventAttendanceStore(store => ({
      importRegistrationsModalOpen: store.importRegistrationsModalOpen,
      setImportRegistrationsModalOpen: store.setImportRegistrationsModalOpen,
    }))

  const [fileHeaders, setFileHeaders] = useState<string[]>([])
  const [fileContent, setFileContent] = useState('')
  const [attributeMapping, setAttributeMapping] = useState<Record<string, string>>({})
  const [isMappingModalVisible, setIsMappingModalVisible] = useState(false)
  const [isImportButtonDisabled, setIsImportButtonDisabled] = useState(true)
  const [isContinueButtonDisabled, setIsContinueButtonDisabled] = useState(true)
  const [isReviewModalVisible, setIsReviewModalVisible] = useState(false)
  const [apiResponse, setApiResponse] = useState<any>(null)
  const [isResultModalVisible, setIsResultModalVisible] = useState(false)
  const [isLogsModalVisible, setIsLogsModalVisible] = useState(false)
  const [fileList, setFileList] = useState<any[]>([])

  const importMutation = useImportRegistrations()

  const handleFileUpload = ({ file }: any) => {
    const reader = new FileReader()
    reader.onload = e => {
      const binaryStr = e.target?.result
      const workbook = read(binaryStr, { type: 'binary', cellText: false, cellDates: true })
      const sheet = workbook.Sheets[workbook.SheetNames[0]]
      const csvData = utils.sheet_to_csv(sheet, { rawNumbers: true })
      const firstLine = csvData.split('\n')[0]
      const parsedHeaders: string[] = []
      let cur = '', inQ = false
      for (const ch of firstLine) {
        if (ch === '"') { inQ = !inQ }
        else if (ch === ',' && !inQ) { parsedHeaders.push(cur.trim()); cur = '' }
        else { cur += ch }
      }
      parsedHeaders.push(cur.trim())
      setFileHeaders(parsedHeaders)
      setFileContent(csvData)
      setIsImportButtonDisabled(false)
      successMessage(`${file.name} file uploaded successfully`)
    }
    reader.onerror = () => failureMessage(`${file.name} file upload failed.`)

    if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      reader.readAsBinaryString(file)
    } else {
      reader.readAsText(file)
    }
  }

  const uploadProps = {
    beforeUpload: (file: any) => {
      handleFileUpload({ file })
      return false
    },
  }

  const resetMapping = () => {
    const newMapping: Record<string, string> = {}
    REGISTRATION_ATTRS.forEach(attr => { newMapping[attr.key] = 'Do not import' })
    setAttributeMapping(newMapping)
  }

  const handleBack = () => {
    resetMapping()
    setIsMappingModalVisible(false)
  }

  useEffect(() => {
    const allRequiredMapped = REGISTRATION_ATTRS
      .filter(attr => attr.required)
      .every(attr => attributeMapping[attr.key] && attributeMapping[attr.key] !== 'Do not import')
    setIsContinueButtonDisabled(!allRequiredMapped)
  }, [attributeMapping])

  const handleAttributeChange = (attribute: string, value: string) => {
    setAttributeMapping({ ...attributeMapping, [attribute]: value })
  }

  const handleReview = () => {
    setIsMappingModalVisible(false)
    setIsReviewModalVisible(true)
  }

  const handleImport = () => {
    // Normalize any date Excel may have reformatted (e.g. 6/24/2026 or 24/6/2026) back to YYYY-MM-DD
    const normalizeDate = (d: string): string => {
      const s = d.trim()
      if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
      const parts = s.split('/')
      if (parts.length === 3) {
        const [a, b, c] = parts
        // M/D/YYYY (Excel US format)
        if (c.length === 4) return `${c}-${a.padStart(2,'0')}-${b.padStart(2,'0')}`
      }
      return s
    }

    const parseCSVLine = (line: string): string[] => {
      const result: string[] = []
      let current = '', inQuotes = false
      for (const char of line) {
        if (char === '"') { inQuotes = !inQuotes }
        else if (char === ',' && !inQuotes) { result.push(current.trim()); current = '' }
        else { current += char }
      }
      result.push(current.trim())
      return result
    }

    const lines = fileContent.split('\n')
    const headers = parseCSVLine(lines[0])
    const rows: any[] = []

    for (let i = 1; i < lines.length; i++) {
      if (!lines[i].trim()) continue
      const values = parseCSVLine(lines[i])
      const mappedRow: any = {}
      let hasValidData = false

      REGISTRATION_ATTRS.forEach(attr => {
        const csvColumn = attributeMapping[attr.key]
        if (!csvColumn || csvColumn === 'Do not import') return
        const colIdx = headers.indexOf(csvColumn)
        if (colIdx === -1) return
        let value: any = values[colIdx] ?? ''
        if (value === '' && !attr.required) return
        if ((attr.key === 'days' || attr.key === 'additionalDays') && value) {
          value = String(value).split(',').map((d: string) => normalizeDate(d)).filter(Boolean)
        }
        mappedRow[attr.key] = value
        if (value) hasValidData = true
      })

      if (hasValidData) {
        // Convert additionalTicketId + additionalDays into additionalTickets array
        if (mappedRow.additionalTicketId) {
          mappedRow.additionalTickets = [{
            ticketId: String(mappedRow.additionalTicketId).trim(),
            days: Array.isArray(mappedRow.additionalDays) ? mappedRow.additionalDays : [],
          }]
        }
        delete mappedRow.additionalTicketId
        delete mappedRow.additionalDays
        rows.push(mappedRow)
      }
    }

    // Validate that every row's eventId matches the current event.
    // Only check rows where eventId looks like a real MongoDB ObjectId (24-char hex) —
    // the sample file has a "Required/Optional" notes row that would otherwise false-positive here.
    const isObjectId = (s: string) => /^[0-9a-f]{24}$/i.test(s)
    const mismatchedRows = rows
      .map((row, idx) => ({ row, idx }))
      .filter(({ row }) => row.eventId && isObjectId(row.eventId) && row.eventId !== eventId)
    if (mismatchedRows.length > 0) {
      failureMessage(
        `Event ID mismatch on ${mismatchedRows.length} row(s). This CSV was exported for a different event. Please use the correct file.`
      )
      return
    }

    // Strip eventId / eventName from rows before sending — they are not API fields
    const apiRows = rows.map(({ eventId: _eid, eventName: _ename, ...rest }: any) => rest)

    importMutation.mutate(
      { eventId, rows: apiRows },
      {
        onSuccess: data => {
          setIsReviewModalVisible(false)
          setApiResponse(data)
          setIsResultModalVisible(true)
        },
      },
    )
  }

  const downloadCSV = () => {
    const errors = apiResponse?.errors || []
    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [['Row', 'Reason'], ...errors.map((e: any) => [e.rowIndex + 1, e.reason])]
        .map((r: any[]) => r.join(','))
        .join('\n')
    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', 'import-registration-errors.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const downloadLog = () => {
    const entries = apiResponse?.log || []
    if (!entries.length) return
    const headers = ['Registration ID', 'Family ID', 'Family Name', 'Person ID', 'Person Name', 'Person Type', 'Organization ID', 'Organization Name', 'Admission Ticket ID', 'Admission Ticket Name', 'Admission Days', 'Additional Tickets', 'Total Value']
    const keys = ['registrationId', 'familyId', 'familyName', 'personId', 'personName', 'personType', 'organizationId', 'organizationName', 'admissionTicketId', 'admissionTicketName', 'admissionDays', 'additionalTickets', 'totalValue']
    const rows = entries.map((e: any) => keys.map(k => `"${String(e[k] ?? '').replace(/"/g, '""')}"`))
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers, ...rows].map(r => r.join(',')).join('\n')
    const link = document.createElement('a')
    link.setAttribute('href', encodeURI(csvContent))
    link.setAttribute('download', 'import-registration-log.csv')
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const onClose = () => {
    setImportRegistrationsModalOpen(false)
    setFileHeaders([])
    setFileContent('')
    setAttributeMapping({})
    setIsImportButtonDisabled(true)
    setFileList([])
    setApiResponse(null)
  }

  return (
    <>
      {/* Modal 1 — Upload */}
      <Modal
        open={importRegistrationsModalOpen}
        footer={null}
        onCancel={onClose}
        className="import-modal"
      >
        <div className="popup-header">
          <h2 className="importText">Import Registrations</h2>
          <img src={Cancel} alt="close" onClick={onClose} />
        </div>
        <p className="attributeText" style={{ marginBottom: 12 }}>
          Use the exported Ticket IDs &amp; Days as reference. Event ID is auto-filled from the current event.
        </p>
        <Upload.Dragger
          {...uploadProps}
          accept=".csv,.xlsx,.xls"
          fileList={fileList}
          onChange={info => setFileList(info.fileList)}
        >
          <p className="ant-upload-drag-icon">
            <img alt="upload" src={VectorImg} />
          </p>
          <p className="dragText">
            Drag and drop document here or
            <b className="uploadText"> upload document</b>
          </p>
          <p className="ant-upload-hint" style={{ marginTop: 10 }}>
            CSV, XLSX or XLS formats
          </p>
        </Upload.Dragger>
        <div className="flex justify-end w-full mt-20">
          <button
            className="prim-sm-btn"
            style={{ background: '#EBEDF1', color: 'black' }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="prim-sm-btn"
            disabled={isImportButtonDisabled}
            onClick={() => setIsMappingModalVisible(true)}
          >
            Import
          </button>
        </div>
      </Modal>

      {/* Modal 2 — Field Mapping */}
      <Modal
        open={isMappingModalVisible}
        footer={null}
        onCancel={handleBack}
        className="mapping-modal"
      >
        <div>
          <div className="popup-header-2">
            <h2>Customize your attributes</h2>
            <img src={Cancel} alt="close" onClick={handleBack} />
          </div>
          <p className="attributeText">
            Select the correct attribute for each column you'd like to import, or create a new one.
          </p>
          <p className="schema-Text">Schema mapping</p>
          <div className="schema-mapping-header">
            <span className="attribute-header">Attribute</span>
            <span className="csv-column-header">CSV column</span>
          </div>
          <div className="schema-mapping h-25rem overflow-auto">
            {REGISTRATION_ATTRS.map(attr => (
              <div key={attr.key} className="mapping-row">
                <div className="fleximg">
                  <img alt="csv" src={CsvImg} style={{ marginRight: 5 }} />
                  <span
                    className={`attribute-label position-relative ${attr.required ? 'required-input' : ''}`}
                  >
                    {attr.display}
                  </span>
                </div>
                <Select
                  value={attributeMapping[attr.key] || 'Do not import'}
                  className="csv-column-select"
                  onChange={value => handleAttributeChange(attr.key, value)}
                >
                  <Option value="Do not import">Do not import</Option>
                  {fileHeaders.map(header => (
                    <Option key={header} value={header}>
                      {header}
                    </Option>
                  ))}
                </Select>
              </div>
            ))}
          </div>
          <div className="flex justify-end w-full mt-20">
            <button
              className="prim-sm-btn"
              style={{ background: '#EBEDF1', color: 'black' }}
              onClick={handleBack}
            >
              Back
            </button>
            <button
              className="prim-sm-btn"
              disabled={isContinueButtonDisabled}
              onClick={handleReview}
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal 3 — Review */}
      <Modal
        open={isReviewModalVisible}
        footer={null}
        onCancel={() => setIsReviewModalVisible(false)}
        className="review-modal"
      >
        <div>
          <div className="popup-header-2">
            <h2>Review values</h2>
            <img
              src={Cancel}
              alt="close"
              onClick={() => {
                setIsReviewModalVisible(false)
                setIsMappingModalVisible(true)
              }}
            />
          </div>
          <p className="attributeText">
            We've created the values we found in your data. Check that the values are correct and
            resolve any that are invalid.
          </p>
          <p className="schema-Text">Schema mapping</p>
          <div className="schema-mapping-header">
            <span className="attribute-header">Attribute</span>
            <span className="csv-column-header">CSV column</span>
          </div>
          <div className="schema-mapping h-25rem overflow-auto">
            {Object.keys(attributeMapping).map(
              key =>
                attributeMapping[key] !== 'Do not import' && (
                  <div key={key} className="mapping-row">
                    <div className="fleximg-2">
                      <img alt="check" src={CheckImg} style={{ marginRight: 10 }} />
                      <img alt="csv" src={CsvImg} style={{ marginRight: 8 }} />
                      <span className="attribute-label position-relative">
                        {REGISTRATION_ATTRS.find(attr => attr.key === key)?.display}
                      </span>
                    </div>
                    <span className="csv-column-label">
                      {attributeMapping[key].replace(/"/g, '')}
                    </span>
                  </div>
                ),
            )}
          </div>
          <div className="flex justify-end w-full mt-20">
            <button
              className="prim-sm-btn"
              style={{ background: '#EBEDF1', color: 'black', marginRight: 10 }}
              onClick={() => {
                setIsReviewModalVisible(false)
                setIsMappingModalVisible(true)
              }}
            >
              Back
            </button>
            <button className="prim-sm-btn" onClick={handleImport}>
              {importMutation.isLoading ? <Loader /> : 'Continue'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal 4 — Result */}
      <Modal
        open={isResultModalVisible}
        onCancel={() => setIsResultModalVisible(false)}
        footer={null}
        className="completeModal"
      >
        <div className="popup-header-2">
          <h2>Complete import</h2>
          <img
            src={Cancel}
            alt="close"
            onClick={() => setIsResultModalVisible(false)}
          />
        </div>
        <div className="d-flex mt-20">
          <div className="recordFill">
            <div className="flex">
              <img src={warningImg} alt="failed" />
              <span className="failedText">{apiResponse?.errors?.length ?? 0}</span>
            </div>
            <span className="text">Records failed</span>
          </div>
          <div className="recordFill" style={{ marginLeft: 10 }}>
            <div className="flex">
              <img src={plusImg} alt="imported" />
              <span className="SuccessText">{apiResponse?.imported ?? 0}</span>
            </div>
            <span className="text">Records imported</span>
          </div>
        </div>
        <div className="flex justify-end w-full mt-20">
          {apiResponse?.log?.length > 0 && (
            <button
              className="prim-sm-btn"
              style={{
                background: 'transparent',
                border: '1px solid #52c41a',
                color: '#52c41a',
                marginRight: 10,
              }}
              onClick={downloadLog}
            >
              Download import log
            </button>
          )}
          {apiResponse?.errors?.length > 0 && (
            <button
              className="prim-sm-btn"
              style={{
                background: 'transparent',
                border: '1px solid #2BA7CA',
                color: '#2BA7CA',
                marginRight: 10,
              }}
              onClick={() => setIsLogsModalVisible(true)}
            >
              View errors
            </button>
          )}
          <button className="prim-sm-btn" onClick={onClose}>
            Done
          </button>
        </div>
      </Modal>

      {/* Modal 5 — Logs */}
      <Modal open={isLogsModalVisible} footer={null} className="logsModal">
        <div className="popup-header-2">
          <h2>Logs</h2>
          <img
            src={Cancel}
            alt="close"
            onClick={() => setIsLogsModalVisible(false)}
          />
        </div>
        <div className="schema-mapping h-25rem overflow-auto">
          <table>
            <thead>
              <tr>
                <th colSpan={1}></th>
                <th>Row</th>
                <th colSpan={10}></th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {apiResponse?.errors?.map((err: any, index: number) => (
                <tr key={index}>
                  <td colSpan={1}></td>
                  <td style={{ textAlign: 'center' }} className="errorMessage">
                    {err.rowIndex + 1}
                  </td>
                  <td colSpan={10}></td>
                  <td className="errorMessage">{err.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex justify-end w-full mt-20">
          <button
            className="prim-sm-btn"
            style={{
              background: 'transparent',
              border: '1px solid #2BA7CA',
              color: '#2BA7CA',
              marginRight: 10,
            }}
            onClick={() => setIsLogsModalVisible(false)}
          >
            Back
          </button>
          <button className="prim-sm-btn" onClick={downloadCSV}>
            Download
          </button>
        </div>
      </Modal>
    </>
  )
}

export default ImportRegistrationsModal
